/**
 * sl-9qm.14: Inline Edit Types
 *
 * Configuration for inline editing in AppBases (internal + public mode).
 * Determines which fields are editable, by whom, and how.
 */
import { z } from 'zod';

import type { CustomAction } from './custom-action.types';
import { customActionsArraySchema } from './custom-action.types';

/** How a field is edited in the view */
export type InlineEditType = 'inline' | 'modal' | 'dropdown' | 'readonly';

/** Configuration for a single editable field */
export interface EditableFieldConfig {
  /** Database column ID */
  column: string;
  /** How this field is edited */
  editType: InlineEditType;
  /** Optional: custom label (defaults to column name) */
  label?: string;
}

/** Minimum DocRole required to edit */
export type MinEditRole = 'Editor' | 'Manager' | 'Owner';

/** Complete inline editing configuration (stored in ViewConfig.settings.inlineEdit) */
export interface InlineEditConfig {
  enabled: boolean;
  editableFields: EditableFieldConfig[];
  minEditRole: MinEditRole;
  optimistic: boolean;
  /** Row-level custom actions (sl-9qm.14.3) */
  customActions?: CustomAction[];
}

/** Zod schema for EditableFieldConfig */
export const editableFieldConfigSchema = z.object({
  column: z.string().min(1),
  editType: z.enum(['inline', 'modal', 'dropdown', 'readonly']),
  label: z.string().optional(),
});

/** Zod schema for InlineEditConfig validation */
export const inlineEditConfigSchema = z.object({
  enabled: z.boolean().default(false),
  editableFields: z.array(editableFieldConfigSchema).default([]),
  minEditRole: z.enum(['Editor', 'Manager', 'Owner']).default('Editor'),
  optimistic: z.boolean().default(true),
  customActions: customActionsArraySchema.optional(),
});

/** Default inline edit configuration */
export const DEFAULT_INLINE_EDIT_CONFIG: InlineEditConfig = {
  enabled: false,
  editableFields: [],
  minEditRole: 'Editor',
  optimistic: true,
};
